/**
 * useTenantSearchParams — react-router's useSearchParams, with a setter that
 * cannot break tenant context
 *
 * @module lib/navigation/useTenantSearchParams
 */

import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { PRESERVED_QUERY_PARAMS } from '../queryParams';

/** A param patch: a value sets the key, `null` deletes it. */
export type SearchParamsPatch = Record<string, string | number | null>;

export interface SetTenantSearchParamsOptions {
  /** Add a history entry instead of replacing the current one (default: replace). */
  replace?: boolean;
}

const RESERVED_KEYS: readonly string[] = PRESERVED_QUERY_PARAMS;

/**
 * Read and write the current page's query params without touching tenant context
 *
 * The read half is the current location's params. The setter is the way to
 * write page-local params (`?plan=`, a flow's selection) and it holds four
 * guarantees so callers don't have to:
 *
 * - **Merge, never replace.** The patch is applied onto the current URL, so
 *   every key it doesn't name — tenant context included — is left alone. The
 *   base is this render's location (as with react-router's own updater form):
 *   change several keys in ONE patch rather than two calls in one handler.
 * - **No history entry by default.** `replace: true` unless asked otherwise, so
 *   Back leaves the page rather than stepping through every selection.
 * - **`location.state` and the hash survive.** A plain `setSearchParams` drops
 *   both: the state carries data some pages render from, and the hash is what
 *   opens the shared sign-in modal (`useHashModal`).
 * - **`tenant-handle` / `club-id` are refused.** They change only through a
 *   full-page tenant switch (`buildClubEntryUrl`). Dev and tests throw so the
 *   mistake surfaces at once; a production build drops the key and logs, so a
 *   member's click never crashes.
 * 
 * **Never call it after navigating away in the same handler.** It writes
 * against this render's location, so it would replace the entry the navigation
 * just pushed (and its state). That is why `usePromoCodeCapture`'s scrub — which
 * runs right after a purchase's confirmation navigate — stays a raw
 * `history.replaceState`.
 *
 * @example
 * ```tsx
 * const [searchParams, setParams] = useTenantSearchParams();
 * const plan = searchParams.get(QueryParams.PLAN);
 *
 * setParams({ [QueryParams.PLAN]: 'membership-5' }); // set
 * setParams({ [QueryParams.PLAN]: null });           // delete
 * setParams({ step: 2 }, { replace: false });        // opt into a history entry
 * ```
 */
export function useTenantSearchParams() {
  const navigate = useNavigate();
  const { search, hash, state } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const setParams = useCallback(
    (patch: SearchParamsPatch, options: SetTenantSearchParamsOptions = {}) => {
      const { replace = true } = options;
      const next = new URLSearchParams(search);

      for (const [key, value] of Object.entries(patch)) {
        if (RESERVED_KEYS.includes(key)) {
          const message = `[useTenantSearchParams] Refusing to write "${key}" — tenant context changes only through a full-page tenant switch (buildClubEntryUrl).`;
          if (import.meta.env.DEV) throw new Error(message);
          console.error(message);
          continue;
        }
        if (value === null) {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      }

      const nextSearch = next.toString();
      navigate(
        { search: nextSearch ? `?${nextSearch}` : '', hash },
        { replace, state },
      );
    },
    [navigate, search, hash, state],
  );

  return [searchParams, setParams] as const;
}
